import type { ApiFlowsExecutionsResponseType } from "@/types/entities/flows";
import axios from "@/utils/axios";
import { adaptivePollingInterval } from "@/utils/polling";
import type { ApiNode } from "@/workflow_api/types/core";

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

export const useRetryDocumentGeneration = (
  setExecutionDetailsOpen: (open: boolean) => void,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      nodeExecutionId,
    }: {
      id: string | undefined;
      nodeExecutionId: string | undefined;
    }) => {
      const response = await axios.post(
        `/admin/v1/flows/${id}/node-executions/${nodeExecutionId}/retry-document-generator`,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["flows"],
      });
      queryClient.resetQueries({
        queryKey: ["flow-executions"],
      });
      queryClient.resetQueries({
        queryKey: ["flow-executions-infinite"],
      });
      setExecutionDetailsOpen(false);
    },
    onError: (error) => {
      // Handle error here, e.g., show a notification
      console.error("Error retrying node:", error);
    },
  });
};

/**
 * Obtiene los datos completos de un nodo con todas las relaciones necesarias
 * para la deserialización del campo calculado
 */
const getNodeWithCalculatedFieldData = async (
  flowId: string,
  nodeId: string,
): Promise<ApiNode | null> => {
  const response = await axios.get(
    `/admin/v1/flows/${flowId}?relationships=${[
      "nodes",
      "nodes.edge",
      "nodes.edges",
      "nodes.targetConnections",
      "nodes.inputs",
      "nodes.inputs.conditions",
      "nodes.inputs.calculations",
      "nodes.inputs.calculations.inputs.sourceConnections.field",
      "nodes.inputs.conditions.input.sourceConnections.field",
      "nodes.inputs.calculations.inputs.targetConnections.field",
      "nodes.inputs.conditions.input.targetConnections.field",
      "nodes.inputs.conditions.optionalInput",
      "nodes.inputs.conditions.optionalInput.sourceConnections.field",
      "nodes.inputs.conditions.optionalInput.targetConnections.field",
    ].join(",")}`,
  );

  const flowData = response.data;
  const node = flowData.data.nodes?.find((n: ApiNode) => n.id === nodeId);

  return node || null;
};

export const useGetNodeWithCalculatedFieldData = () => {
  return useMutation({
    mutationFn: async ({
      flowId,
      nodeId,
    }: {
      flowId: string;
      nodeId: string;
    }): Promise<ApiNode | null> => {
      return await getNodeWithCalculatedFieldData(flowId, nodeId);
    },
    onError: (error) => {
      console.error("Error obtaining node with calculated field data:", error);
    },
  });
};

export const useGetFlowExecutionsAsInfinite = (params: {
  flow_id: string;
  status?: string;
  context?: string;
  applicant_id?: string;
  created_at?: string;
  search?: string;
}) => {
  return useInfiniteQuery({
    queryKey: ["flow-executions-infinite", params],
    enabled: !!params.flow_id,
    queryFn: async ({
      pageParam = 1,
    }): Promise<ApiFlowsExecutionsResponseType> => {
      const response = await axios.get(`/admin/v1/flows/executions`, {
        params: {
          page: pageParam,
          per_page: 20,
          relationships: "formalityRequests",
          filter: {
            flow_id: params.flow_id,
            ...(params.status && { status: params.status }),
            ...(params.context && { context: params.context }),
            ...(params.applicant_id && { applicant_id: params.applicant_id }),
            ...(params.created_at && { created_at: params.created_at }),
            ...(params.search && { search: params.search }),
          },
        },
      });
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      const { current_page, last_page } = (
        lastPage as {
          meta: { current_page: number; last_page: number };
        }
      ).meta;
      if (current_page < last_page) {
        return current_page + 1;
      }
    },
    initialPageParam: 1,
  });
};

export const useGetFlowExecutions = (params: {
  flow_id: string;
  status?: string;
  context?: string;
  applicant_id?: string;
  created_at?: string;
  search?: string;
  page?: number;
}) => {
  return useQuery({
    queryKey: ["flow-executions", params],
    enabled: !!params.flow_id,
    staleTime: 500,
    queryFn: async (): Promise<ApiFlowsExecutionsResponseType> => {
      const response = await axios.get(`/admin/v1/flows/executions`, {
        params: {
          per_page: 20,
          relationships: "formalityRequests",
          filter: {
            flow_id: params.flow_id,
            ...(params.status && { status: params.status }),
            ...(params.context && { context: params.context }),
            ...(params.applicant_id && { applicant_id: params.applicant_id }),
            ...(params.created_at && { created_at: params.created_at }),
            ...(params.search && { search: params.search }),
          },
        },
      });
      return response.data;
    },
  });
};

export const useGetExecution = (
  id: string,
  refreshInterval: number = 0,
  subflowId?: string,
  canViewFlows?: boolean,
) => {
  return useQuery({
    queryKey: ["flow-execution", id, subflowId],
    enabled: !!id && canViewFlows,
    staleTime: 500,
    queryFn: async (): Promise<{
      data: ApiFlowsExecutionsResponseType["data"][number];
    }> => {
      const response = await axios.get(`/admin/v1/flows/executions/${id}`, {
        params: {
          relationships: [
            "nodeExecutions",
            "nodeExecutions.formAssignments",
            "nodeExecutions.formAssignments.revisions",
            "nodeExecutions.formAssignments.filledForm",
            "nodeExecutions.userFormAssignments",
            "nodeExecutions.userFormAssignments.filledForm",
            "nodeExecutions.userFormAssignments.form",
            "nodeExecutions.formAssignments.form",
            "nodeExecutions.inputs",
            "nodeExecutions.outputs",
            "nodeExecutions.node.inputs",
            "nodeExecutions.node.targetConnections",
            "nodeExecutions.inputs.conditions.input",
            "nodeExecutions.inputs.conditions.optionalInput",
            "nodeExecutions.inputs.calculations.inputs",
            "nodeExecutions.inputs.grouping.candidates",
            "nodeExecutions.paymentRequest",
            "nodeExecutions.interactions",
            "nodeExecutions.interactions.applicant",
            "nodeExecutions.interactions.onBehalfOf",
            "formalityRequests",
            "formalityRequests.applicant",
            "formalityRequests.groups",
            "formalityRequests.assignedTo",
            "formalityRequests.assignedBy",
            "formalityRequests.process",
          ].join(","),
        },
      });
      return {
        ...response.data,
        data: {
          ...response.data.data,
          node_executions: response.data.data.node_executions.filter(
            (ne: { flow_execution_id: string }) => {
              return ne.flow_execution_id === subflowId || !subflowId;
            },
          ),
        },
      };
    },
    ...(refreshInterval > 0 && { refetchInterval: adaptivePollingInterval }),
  });
};
